import LZString from 'lz-string';
import { DropboxService } from './DropboxService';
import { GoogleDriveService } from './GoogleDriveService';
import { isMobileDevice } from '../utils/deviceDetection';

export interface LoadedFile {
  id: string;
  file: File;
  originalUrl?: string;
  shareableUrl?: string;
}

import { Region } from '../context/RegionsContext';

export interface ShareData {
  tracks: Array<{
    id: number;
    shareableUrl: string;
    name?: string;
  }>;
  notes: Record<string, string>;
  displayNames?: Record<string, string>;
  regions?: Record<string, Region[]>; // Regions keyed by track position ("1", "2", "3")
  timestamp: number;
  isOwner: boolean;
  allowDownloads: boolean;
  allowFileSelection?: boolean; // Allow recipients to select which files to load
  originalUrls: string[];
  pageTitle?: string;
  totalFileSize?: number; // Total size of all files in bytes
}

interface ProcessedFile {
  file: File;
  originalUrl: string;
  shareableUrl: string;
}

/**
 * Service for handling file operations
 */
export class FileService {
  private files: ProcessedFile[] = [];
  private selectedPlayer?: number;
  private isLoading = false;

  /**
   * Infer a reasonable audio MIME type from a filename or URL
   */
  static inferMimeType(nameOrUrl: string): string | undefined {
    try {
      const lower = nameOrUrl.toLowerCase();
      // Try to extract extension from either a plain name or URL path
      const url = new URL(lower, lower.startsWith('http') ? undefined : 'http://local');
      const pathname = url.pathname || lower;
      const match = pathname.match(/\.([a-z0-9]+)$/);
      const ext = match ? match[1] : '';
      switch (ext) {
        case 'mp3':
          return 'audio/mpeg';
        case 'm4a':
        case 'mp4':
          return 'audio/mp4';
        case 'wav':
          return 'audio/wav';
        case 'ogg':
        case 'oga':
          return 'audio/ogg';
        case 'webm':
          return 'audio/webm';
        case 'aac':
          return 'audio/aac';
        case 'flac':
          return 'audio/flac';
        case 'aif':
        case 'aiff':
          return 'audio/aiff';
        default:
          return undefined;
      }
    } catch {
      // Fallback: no type hint
      return undefined;
    }
  }

  /**
   * Converts a Dropbox shared link to a direct download link without requiring authentication
   * @param url - The Dropbox shared URL
   * @returns A direct download URL
   */
  static convertToDirectDownloadUrl(url: string): string {
    // Make sure we're using dl.dropboxusercontent.com instead of www.dropbox.com
    let directUrl = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    
    // Mobile browsers work better with dl=0, desktop prefers dl=1
    const isMobile = isMobileDevice();
    const dlParam = isMobile ? 'dl=0' : 'dl=1';
    
    // Ensure the dl parameter is set correctly
    if (directUrl.includes('?')) {
      // URL already has parameters
      if (directUrl.includes('dl=0')) {
        directUrl = directUrl.replace('dl=0', dlParam);
      } else if (directUrl.includes('dl=1')) {
        directUrl = directUrl.replace('dl=1', dlParam);
      } else if (!directUrl.includes('dl=')) {
        directUrl += '&' + dlParam;
      }
    } else {
      // URL has no parameters yet
      directUrl += '?' + dlParam;
    }
    
    console.log(`[FileService] Converted to direct URL: ${directUrl}`);
    return directUrl;
  }

  /**
   * Converts a Google Drive shared link to a direct download URL without requiring authentication
   * @param url - The Google Drive shared URL
   * @returns A direct download URL
   */
  static convertGoogleDriveToDirectDownloadUrl(url: string): string {
    return GoogleDriveService.convertToDirectDownloadUrl(url);
  }

  /**
   * Fetch with mobile fallback to CORS proxy and progress tracking
   * @param url - URL to fetch
   * @param onProgress - Progress callback for bytes downloaded
   * @returns Response promise
   */
  static async fetchWithMobileFallbackAndProgress(
    url: string, 
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<{ response: Response; blob: Blob }> {
    const isMobile = isMobileDevice();
    
    // Try multiple fetch strategies to improve reliability (especially on mobile)
    let response: Response | undefined;
    let lastError: Error | undefined;
    
    // Simple CORS GET only. Custom Accept / User-Agent headers make Safari
    // preflight Dropbox, which then fails with a generic "Load failed".
    try {
      response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'default',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        
        if (total > 0 && onProgress) {
          const reader = response.body?.getReader();
          if (reader) {
            const chunks: Uint8Array[] = [];
            let downloaded = 0;
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              chunks.push(value);
              downloaded += value.length;
              onProgress(downloaded, total);
            }
            
            const blob = new Blob(chunks);
            return { response, blob };
          }
        }
        
        const blob = await response.blob();
        if (blob.size > 1000) {
          return { response, blob };
        }
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.log(`[FileService] Strategy 1 failed, trying retries: ${lastError.message}`);
    }
    
    // Strategy 2: Retry with a different cache policy
    if (!response?.ok) {
      try {
        await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay
        response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          cache: 'force-cache',
          credentials: 'omit'
        });
        if (response.ok) {
          const blob = await response.blob();
          if (blob.size > 1000) {
            console.log(`[FileService] Strategy 2 succeeded: ${blob.size} bytes`);
            return { response, blob };
          }
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.log(`[FileService] Strategy 2 failed: ${lastError.message}`);
        
        // Strategy 3: Final attempt with minimal options
        try {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay
          response = await fetch(url);
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 1000) {
              console.log(`[FileService] Strategy 3 succeeded: ${blob.size} bytes`);
              return { response, blob };
            }
          }
        } catch (finalError) {
          lastError = finalError instanceof Error ? finalError : new Error(String(finalError));
          console.log(`[FileService] Strategy 3 failed: ${lastError.message}`);
        }
      }
    }
    
    // If all strategies failed, try CORS proxy on mobile
    if (isMobile && !response?.ok) {
      console.log(`[FileService] All direct fetch strategies failed, trying CORS proxy: ${lastError?.message}`);
    }
    
    // Mobile fallback: use CORS proxy with progress tracking
    if (isMobile) {
      try {
        const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
        const response = await fetch(proxyUrl);
        if (response.ok) {
          const contentLength = response.headers.get('content-length');
          const total = contentLength ? parseInt(contentLength, 10) : 0;
          
          if (total > 0 && onProgress) {
            // Create a readable stream to track progress for CORS proxy too
            const reader = response.body?.getReader();
            if (reader) {
              const chunks: Uint8Array[] = [];
              let downloaded = 0;
              
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                chunks.push(value);
                downloaded += value.length;
                onProgress(downloaded, total);
              }
              
              const blob = new Blob(chunks);
              console.log(`[FileService] CORS proxy succeeded with progress: ${blob.size} bytes`);
              return { response, blob };
            }
          }
          
          // Fallback to regular blob if progress tracking fails
          const blob = await response.blob();
          if (blob.size > 1000) {
            console.log(`[FileService] CORS proxy succeeded: ${blob.size} bytes`);
            return { response, blob };
          }
        }
      } catch (error) {
        console.log(`[FileService] CORS proxy failed: ${error}`);
      }
    }
    
    throw new Error('All fetch attempts failed');
  }

  /**
   * Fetch with mobile fallback to CORS proxy
   * @param url - URL to fetch
   * @returns Response promise
   */
  static async fetchWithMobileFallback(url: string): Promise<{ response: Response; blob: Blob }> {
    const isMobile = isMobileDevice();
    
    // Try direct fetch first
    try {
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        
        // Basic validation: ensure we got real content, not an error page
        if (blob.size > 1000) { // Audio files should be larger than 1KB
          return { response, blob };
        }
      }
    } catch (error) {
      if (!isMobile) {
        throw error; // Desktop should fail normally
      }
      console.log(`[FileService] Direct fetch failed on mobile, trying CORS proxy: ${error}`);
    }
    
    // Mobile fallback: use CORS proxy
    if (isMobile) {
      try {
        const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
        const response = await fetch(proxyUrl);
        if (response.ok) {
          const blob = await response.blob();
          if (blob.size > 1000) {
            console.log(`[FileService] CORS proxy succeeded: ${blob.size} bytes`);
            return { response, blob };
          }
        }
      } catch (error) {
        console.log(`[FileService] CORS proxy failed: ${error}`);
      }
    }
    
    throw new Error('All fetch attempts failed');
  }

  /**
   * Loads files from a shared setup URL
   * @param data - Compressed and encoded share data from URL
   * @param onProgress - Progress callback for file count
   * @param onBytesProgress - Progress callback for bytes downloaded
   * @returns Promise containing loaded files and share data
   * @throws Error if data is invalid or loading fails
   */
  static async loadSharedSetup(
    data: string,
    onProgress?: (completed: number, total: number) => void,
    onBytesProgress?: (downloadedBytes: number, totalBytes: number) => void
  ): Promise<{ files: LoadedFile[]; shareData: ShareData }> {
    try {
      const decompressedData = LZString.decompressFromEncodedURIComponent(data);
      const shareData = JSON.parse(decompressedData) as ShareData;
      
      if (!shareData.tracks || !Array.isArray(shareData.tracks)) {
        throw new Error('Invalid share data');
      }

      console.log('[FileService] Loading shared setup with tracks:', shareData.tracks);
      
      const dropboxService = DropboxService.getInstance();
      const googleDriveService = GoogleDriveService.getInstance();
      const isDropboxAuthenticated = dropboxService.isAuthenticated();
      const isGoogleDriveAuthenticated = googleDriveService.isAuthenticated();
      const isMobile = isMobileDevice();

      console.log(`[FileService] Authentication state - Dropbox: ${isDropboxAuthenticated}, Google Drive: ${isGoogleDriveAuthenticated}, Mobile: ${isMobile}`);
      
      if (isMobile) {
        console.log('[FileService] Mobile device detected, using simplified approach');
      }
      
      const total = shareData.tracks.length;
      let completed = 0;
      let totalDownloadedBytes = 0;
      let totalBytes = shareData.totalFileSize || 0;
      
      if (onProgress) onProgress(0, total);
      if (onBytesProgress && totalBytes > 0) onBytesProgress(0, totalBytes);

      // Track individual file progress
      const fileProgress = new Map<string, { downloaded: number; total: number }>();

      // Helper function to update bytes progress
      const updateBytesProgress = (trackId: string, downloaded: number, total: number) => {
        fileProgress.set(trackId, { downloaded, total });
        
        // Calculate total downloaded bytes across all files
        let totalDownloaded = 0;
        
        fileProgress.forEach((progress) => {
          totalDownloaded += progress.downloaded;
        });
        
        // Use the upfront totalBytes (from shareData.totalFileSize) instead of recalculating
        // This ensures the spinner shows the correct total size immediately
        if (onBytesProgress) {
          // If we have an upfront total, use it; otherwise fall back to calculated total
          const finalTotal = totalBytes > 0 ? totalBytes : (() => {
            let calculatedTotal = 0;
            fileProgress.forEach((progress) => {
              calculatedTotal += progress.total;
            });
            return calculatedTotal;
          })();
          
          if (finalTotal > 0) {
            onBytesProgress(totalDownloaded, finalTotal);
          }
        }
      };

      const files = await Promise.all(
        shareData.tracks.map(async (track) => {
          try {
            // Use the track name from shared data or a default name
            const trackName = track.name || `track-${track.id}.m4a`;
            console.log(`[FileService] Processing track ${track.id} with name "${trackName}"`);
            
            const isDropboxURL = track.shareableUrl.includes('dropbox.com') || track.shareableUrl.includes('dl.dropboxusercontent.com');
            const isGoogleDriveURL = track.shareableUrl.includes('drive.google.com') || track.shareableUrl.includes('googleusercontent.com');
            
            if (isDropboxURL) {
              // Convert to a direct download URL without requiring authentication
              const directUrl = FileService.convertToDirectDownloadUrl(track.shareableUrl);
              
              // Try direct download with mobile fallback and progress tracking
              try {
                console.log(`[FileService] Trying direct download: ${directUrl}`);
                const { blob } = await FileService.fetchWithMobileFallbackAndProgress(
                  directUrl,
                  (downloaded, total) => updateBytesProgress(track.id.toString(), downloaded, total)
                );
                const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(track.shareableUrl) || '';
                const result = {
                  id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
                  file: new File([blob], trackName, { type: mimeType }),
                  originalUrl: track.shareableUrl,
                  shareableUrl: directUrl
                };
                completed += 1;
                if (onProgress) onProgress(completed, total);
                return result;
              } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.error(`[FileService] Direct download failed: ${errorMessage}`);
                // Continue to fallback methods if direct download fails
              }
              
              // Try alternative URL formats (no auth required for shared links)
              // Try with dl=1 instead of dl=0 (sometimes works better)
              try {
                console.log(`[FileService] Trying alternative URL format for track ${track.id} (dl=1)`);
                const altUrl = track.shareableUrl.replace('dl=0', 'dl=1').replace('&dl=0', '&dl=1');
                if (altUrl !== directUrl) {
                  const { blob } = await FileService.fetchWithMobileFallbackAndProgress(
                    altUrl,
                    (downloaded, total) => updateBytesProgress(track.id.toString(), downloaded, total)
                  );
                  const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(altUrl) || '';
                  const result = {
                    id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
                    file: new File([blob], trackName, { type: mimeType }),
                    originalUrl: track.shareableUrl,
                    shareableUrl: altUrl
                  };
                  completed += 1;
                  if (onProgress) onProgress(completed, total);
                  return result;
                }
              } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.log(`[FileService] Alternative URL format failed: ${errorMessage}`);
              }
              
              // Only try API methods if authenticated (original sharer)
              if (isDropboxAuthenticated) {
                try {
                  console.log(`[FileService] Attempting Dropbox API fallback for track ${track.id} (authenticated)`);
                  // For shared links, getPermanentLinks just converts the URL format
                  const permanentLinks = await dropboxService.getPermanentLinks([{ id: String(track.id), name: trackName, link: track.shareableUrl }]);
                  const permanentUrl = permanentLinks[0].url;
                  
                  // Try to download using the Dropbox API's getFileContent
                  // For shared links, we can use the URL directly (fileId can be the track ID)
                  // getFileContent will use authenticated fetch which might help with CORS
                  const blob = await dropboxService.getFileContent(String(track.id), permanentUrl, (downloaded, total) => {
                    updateBytesProgress(track.id.toString(), downloaded, total);
                  });
                  const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(permanentUrl) || '';
                  const result = {
                    id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
                    file: new File([blob], trackName, { type: mimeType }),
                    originalUrl: track.shareableUrl,
                    shareableUrl: permanentUrl
                  };
                  completed += 1;
                  if (onProgress) onProgress(completed, total);
                  return result;
                } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  console.error(`[FileService] API download failed: ${errorMessage}`);
                  // Fall through to error if API methods fail
                }
              } else {
                console.log(`[FileService] Skipping Dropbox API fallback for track ${track.id} (not authenticated)`);
              }
              
              // If we got here, both direct and API methods failed
              throw new Error(`Failed to download Dropbox file: ${trackName}`);
            }

            if (isGoogleDriveURL) {
              // Convert to a direct download URL without requiring authentication
              const directUrl = FileService.convertGoogleDriveToDirectDownloadUrl(track.shareableUrl);
              
              // Try direct download with mobile fallback and progress tracking
              try {
                console.log(`[FileService] Trying direct download: ${directUrl}`);
                const { blob } = await FileService.fetchWithMobileFallbackAndProgress(
                  directUrl,
                  (downloaded, total) => updateBytesProgress(track.id.toString(), downloaded, total)
                );
                const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(track.shareableUrl) || '';
                const result = {
                  id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
                  file: new File([blob], trackName, { type: mimeType }),
                  originalUrl: track.shareableUrl,
                  shareableUrl: directUrl
                };
                completed += 1;
                if (onProgress) onProgress(completed, total);
                return result;
              } catch (error) {
                console.error(`[FileService] Direct download failed: ${error}`);
                // Continue to fallback methods if direct download fails
              }
              
              // Only try API methods if authenticated (original sharer)
              if (isGoogleDriveAuthenticated) {
                try {
                  // Extract file ID from URL
                  const fileId = GoogleDriveService.convertToDirectDownloadUrl(track.shareableUrl).match(/id=([a-zA-Z0-9_-]+)/)?.[1] || 
                                 track.shareableUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)?.[1];
                  
                  if (fileId) {
                    const permanentLinks = await googleDriveService.getPermanentLinks([{ id: fileId, name: trackName, link: track.shareableUrl }]);
                    const permanentUrl = permanentLinks[0].url;
                    
                    // Try to download using the Google Drive API
                    const blob = await googleDriveService.getFileContent(fileId, permanentUrl, (downloaded: number, total: number) => {
                      updateBytesProgress(track.id.toString(), downloaded, total);
                    });
                    const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(permanentUrl) || '';
                    const shareableUrl = (permanentLinks[0] as any).shareableUrl || `https://drive.google.com/file/d/${fileId}/view`;
                    const result = {
                      id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
                      file: new File([blob], trackName, { type: mimeType }),
                      originalUrl: track.shareableUrl,
                      shareableUrl
                    };
                    completed += 1;
                    if (onProgress) onProgress(completed, total);
                    return result;
                  }
                } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  console.error(`[FileService] Google Drive API download failed: ${errorMessage}`);
                  // Fall through to error if API methods fail
                }
              }
              
              // If we got here, both direct and API methods failed
              throw new Error(`Failed to download Google Drive file: ${trackName}`);
            }

            // For non-Dropbox URLs (including direct file links), try direct download with progress tracking
            let blob: Blob;
            try {
              const result = await FileService.fetchWithMobileFallbackAndProgress(
                track.shareableUrl,
                (downloaded, total) => updateBytesProgress(track.id.toString(), downloaded, total)
              );
              blob = result.blob;
            } catch (directError) {
              console.log(`[FileService] Direct fetch failed for ${track.shareableUrl}, trying CORS proxy: ${directError}`);
              
              // Fallback to CORS proxy for direct file links that have CORS restrictions
              try {
                const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(track.shareableUrl);
                const proxyResponse = await fetch(proxyUrl);
                if (!proxyResponse.ok) {
                  throw new Error(`CORS proxy failed with status ${proxyResponse.status}`);
                }
                
                // Track progress for proxy download
                const contentLength = proxyResponse.headers.get('content-length');
                const total = contentLength ? parseInt(contentLength, 10) : 0;
                
                if (total > 0 && onBytesProgress) {
                  const reader = proxyResponse.body?.getReader();
                  if (reader) {
                    const chunks: Uint8Array[] = [];
                    let downloaded = 0;
                    
                    while (true) {
                      const { done, value } = await reader.read();
                      if (done) break;
                      
                      chunks.push(value);
                      downloaded += value.length;
                      updateBytesProgress(track.id.toString(), downloaded, total);
                    }
                    
                    blob = new Blob(chunks);
                    console.log(`[FileService] CORS proxy succeeded: ${blob.size} bytes`);
                  } else {
                    blob = await proxyResponse.blob();
                  }
                } else {
                  blob = await proxyResponse.blob();
                }
              } catch (proxyError) {
                console.error(`[FileService] CORS proxy also failed: ${proxyError}`);
                throw new Error(`Failed to download file: ${directError instanceof Error ? directError.message : 'Network error'}`);
              }
            }
            
            const mimeType = FileService.inferMimeType(trackName) || FileService.inferMimeType(track.shareableUrl) || '';
            const result = {
              id: track.id ? String(track.id) : `${Date.now()}-${track.id}`,
              file: new File([blob], trackName, { type: mimeType }),
              originalUrl: track.shareableUrl,
              shareableUrl: track.shareableUrl
            };
            completed += 1;
            if (onProgress) onProgress(completed, total);
            return result;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorStack = error instanceof Error ? error.stack : undefined;
            console.error(`Error loading file track-${track.id}: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
            throw error;
          }
        })
      );

      return { files, shareData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error(`Error loading shared setup: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
      throw error;
    }
  }

  /**
   * Loads a shared setup with selective file loading
   * @param data - Compressed share data
   * @param selectedFileIds - Array of file IDs to load
   * @param onProgress - Progress callback for files completed
   * @param onBytesProgress - Progress callback for bytes downloaded
   * @returns Object containing loaded files and share data
   */
  static async loadSharedSetupSelective(
    data: string,
    selectedFileIds: string[],
    onProgress?: (completed: number, total: number) => void,
    onBytesProgress?: (downloadedBytes: number, totalBytes: number) => void
  ): Promise<{ files: LoadedFile[]; shareData: ShareData }> {
    try {
      console.log('[FileService] Loading shared setup with selective files:', selectedFileIds);
      
      const decompressedData = LZString.decompressFromEncodedURIComponent(data);
      const shareData = JSON.parse(decompressedData) as ShareData;
      
      if (!shareData.tracks || !Array.isArray(shareData.tracks)) {
        throw new Error('Invalid share data');
      }

      // Filter tracks to only include the selected file IDs
      const tracksToLoad = shareData.tracks.filter(track => selectedFileIds.includes(track.id.toString()));
      console.log('[FileService] Loading selected tracks:', tracksToLoad.map(t => ({ id: t.id, name: t.name })));
      
      const dropboxService = DropboxService.getInstance();
      const isAuthenticated = dropboxService.isAuthenticated();
      const isMobile = isMobileDevice();

      if (isMobile) {
        console.log('[FileService] Mobile device detected, using simplified approach');
      }
      
      const total = tracksToLoad.length;
      let completed = 0;
      let totalDownloadedBytes = 0;
      let totalBytes = 0;

      // Calculate total bytes for selected files only
      for (const track of tracksToLoad) {
        try {
          const response = await fetch(track.shareableUrl, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            totalBytes += parseInt(contentLength, 10);
          }
        } catch (error) {
          console.warn(`Could not get size for track ${track.id}:`, error);
        }
      }
      
      if (onProgress) onProgress(0, total);
      if (onBytesProgress && totalBytes > 0) onBytesProgress(0, totalBytes);

      // Track individual file progress
      const fileProgress = new Map<string, { downloaded: number; total: number }>();

      // Helper function to update bytes progress
      const updateBytesProgress = (trackId: string, downloaded: number, total: number) => {
        fileProgress.set(trackId, { downloaded, total });
        
        // Calculate total downloaded bytes across all files
        let totalDownloaded = 0;
        
        fileProgress.forEach((progress) => {
          totalDownloaded += progress.downloaded;
        });
        
        // Use the upfront totalBytes (calculated from HEAD requests) instead of recalculating
        // This ensures the spinner shows the correct total size immediately
        if (onBytesProgress) {
          // If we have an upfront total, use it; otherwise fall back to calculated total
          const finalTotal = totalBytes > 0 ? totalBytes : (() => {
            let calculatedTotal = 0;
            fileProgress.forEach((progress) => {
              calculatedTotal += progress.total;
            });
            return calculatedTotal;
          })();
          
          if (finalTotal > 0) {
            onBytesProgress(totalDownloaded, finalTotal);
          }
        }
      };

      const files = await Promise.all(
        tracksToLoad.map(async (track) => {
          try {
            console.log(`[FileService] Loading track-${track.id} from ${track.shareableUrl}`);
            
            let file: File;
            
            if (isMobile) {
              // Mobile: Use simplified approach
              const response = await fetch(track.shareableUrl);
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              const blob = await response.blob();
              const mimeType = FileService.inferMimeType(track.shareableUrl) || 'audio/mpeg';
              file = new File([blob], track.name || `track-${track.id}`, { type: mimeType });
            } else {
              // Desktop: Try direct fetch first, then CORS proxy if needed
              let blob: Blob;
              try {
                const result = await FileService.fetchWithMobileFallbackAndProgress(
                  track.shareableUrl,
                  (downloaded: number, total: number) => updateBytesProgress(track.id.toString(), downloaded, total)
                );
                blob = result.blob;
              } catch (directError) {
                console.log(`[FileService] Direct fetch failed for ${track.shareableUrl}, trying CORS proxy: ${directError}`);
                
                // Fallback to CORS proxy for direct file links that have CORS restrictions
                try {
                  const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(track.shareableUrl);
                  const proxyResponse = await fetch(proxyUrl);
                  if (!proxyResponse.ok) {
                    throw new Error(`CORS proxy failed with status ${proxyResponse.status}`);
                  }
                  
                  // Track progress for proxy download
                  const contentLength = proxyResponse.headers.get('content-length');
                  const total = contentLength ? parseInt(contentLength, 10) : 0;
                  
                  if (total > 0) {
                    const reader = proxyResponse.body?.getReader();
                    if (reader) {
                      const chunks: Uint8Array[] = [];
                      let downloaded = 0;
                      
                      while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        
                        chunks.push(value);
                        downloaded += value.length;
                        updateBytesProgress(track.id.toString(), downloaded, total);
                      }
                      
                      blob = new Blob(chunks);
                      console.log(`[FileService] CORS proxy succeeded: ${blob.size} bytes`);
                    } else {
                      blob = await proxyResponse.blob();
                    }
                  } else {
                    blob = await proxyResponse.blob();
                  }
                } catch (proxyError) {
                  console.error(`[FileService] CORS proxy also failed: ${proxyError}`);
                  throw new Error(`Failed to download file: ${directError instanceof Error ? directError.message : 'Network error'}`);
                }
              }
              
              const mimeType = FileService.inferMimeType(track.shareableUrl) || 'audio/mpeg';
              file = new File([blob], track.name || `track-${track.id}`, { type: mimeType });
            }

            completed++;
            if (onProgress) onProgress(completed, total);
            
            console.log(`[FileService] Successfully loaded track-${track.id}: ${file.name} (${file.size} bytes)`);
            
            return {
              id: `shared-${track.id}`,
              file,
              originalUrl: track.shareableUrl,
              shareableUrl: track.shareableUrl
            };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorStack = error instanceof Error ? error.stack : undefined;
            console.error(`Error loading file track-${track.id}: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
            throw error;
          }
        })
      );

      return { files, shareData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error(`Error loading shared setup selectively: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
      throw error;
    }
  }

  /**
   * Processes selected files and their URLs
   * @param files - Array of File objects
   * @param urls - Array of original URLs
   * @param shareableUrls - Array of shareable URLs
   * @returns Array of LoadedFile objects
   */
  static handleFilesSelected(files: File[], urls: string[], shareableUrls: string[]): LoadedFile[] {
    const now = Date.now();
    return files.map((file, index) => ({
      id: `${now}-${index}-${file.name}`,
      file,
      originalUrl: urls[index],
      shareableUrl: shareableUrls[index]
    }));
  }

  async loadSharedSetup(shareData: ShareData): Promise<void> {
    try {
      console.log('[FileService] Loading shared setup:', shareData);
      
      if (!shareData.tracks || !Array.isArray(shareData.tracks)) {
        throw new Error('Invalid share data');
      }

      const dropboxService = DropboxService.getInstance();
      const googleDriveService = GoogleDriveService.getInstance();
      const isDropboxAuthenticated = dropboxService.isAuthenticated();
      const isGoogleDriveAuthenticated = googleDriveService.isAuthenticated();
      
      // Process each track in the shared data
      const processedFiles = await Promise.all(
        shareData.tracks.map(async (track, idx) => {
          try {
            // Use the track name from shared data or a default name
            const trackName = track.name || `track-${track.id}.m4a`;
            
            const isDropboxURL = track.shareableUrl.includes('dropbox.com') || track.shareableUrl.includes('dl.dropboxusercontent.com');
            const isGoogleDriveURL = track.shareableUrl.includes('drive.google.com') || track.shareableUrl.includes('googleusercontent.com');
            
            if (isDropboxURL) {
              // Convert to direct URL first
              const directUrl = FileService.convertToDirectDownloadUrl(track.shareableUrl);
              
              // Try direct download first
              try {
                const response = await fetch(directUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  return {
                    id: track.id ? String(track.id) : `${Date.now()}-${idx}`,
                    file: new File([blob], trackName, { type: 'audio/mp4' }),
                    originalUrl: track.shareableUrl,
                    shareableUrl: directUrl
                  };
                }
              } catch (error) {
                console.error(`[FileService] Direct download failed: ${error}`);
                // Continue to fallback methods if direct download fails
              }
              
              // Only use API if authenticated
              if (isDropboxAuthenticated) {
                try {
                  const permanentLinks = await dropboxService.getPermanentLinks([{ id: String(track.id), name: trackName, link: track.shareableUrl }]);
                  const permanentUrl = permanentLinks[0].url;
                  
                  // Try to download using the Dropbox API's getFileContent
                  // For shared links, we can use the URL directly (fileId can be the track ID)
                  const blob = await dropboxService.getFileContent(String(track.id), permanentUrl);
                  return {
                    id: track.id ? String(track.id) : `${Date.now()}-${idx}`,
                    file: new File([blob], trackName, { type: 'audio/mp4' }),
                    originalUrl: track.shareableUrl,
                    shareableUrl: permanentUrl
                  };
                } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  console.error(`[FileService] API download failed: ${errorMessage}`);
                }
              }
              
              // If we got here, both direct and API methods failed
              throw new Error(`Failed to download Dropbox file: ${trackName}`);
            }

            if (isGoogleDriveURL) {
              // Convert to direct URL first
              const directUrl = FileService.convertGoogleDriveToDirectDownloadUrl(track.shareableUrl);
              
              // Try direct download first
              try {
                const response = await fetch(directUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  return {
                    id: track.id ? String(track.id) : `${Date.now()}-${idx}`,
                    file: new File([blob], trackName, { type: 'audio/mp4' }),
                    originalUrl: track.shareableUrl,
                    shareableUrl: directUrl
                  };
                }
              } catch (error) {
                console.error(`[FileService] Direct download failed: ${error}`);
                // Continue to fallback methods if direct download fails
              }
              
              // Only use API if authenticated
              if (isGoogleDriveAuthenticated) {
                try {
                  // Extract file ID from URL
                  const fileId = track.shareableUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)?.[1] ||
                                 track.shareableUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/)?.[1];
                  
                  if (fileId) {
                    const permanentLinks = await googleDriveService.getPermanentLinks([{ id: fileId, name: trackName, link: track.shareableUrl }]);
                    const permanentUrl = permanentLinks[0].url;
                    
                    // Try to download using the Google Drive API
                    const blob = await googleDriveService.getFileContent(fileId, permanentUrl);
                    const shareableUrl = (permanentLinks[0] as any).shareableUrl || `https://drive.google.com/file/d/${fileId}/view`;
                    return {
                      id: track.id ? String(track.id) : `${Date.now()}-${idx}`,
                      file: new File([blob], trackName, { type: 'audio/mp4' }),
                      originalUrl: track.shareableUrl,
                      shareableUrl
                    };
                  }
                } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  console.error(`[FileService] Google Drive API download failed: ${errorMessage}`);
                }
              }
              
              // If we got here, both direct and API methods failed
              throw new Error(`Failed to download Google Drive file: ${trackName}`);
            }

            // For non-Dropbox URLs (including direct file links), try direct download with CORS proxy fallback
            let blob: Blob;
            try {
              const response = await fetch(track.shareableUrl);
              if (!response.ok) {
                throw new Error(`Failed to download file: ${response.statusText}`);
              }
              blob = await response.blob();
            } catch (directError) {
              console.log(`[FileService] Direct fetch failed for ${track.shareableUrl}, trying CORS proxy: ${directError}`);
              
              // Fallback to CORS proxy for direct file links that have CORS restrictions
              try {
                const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(track.shareableUrl);
                const proxyResponse = await fetch(proxyUrl);
                if (!proxyResponse.ok) {
                  throw new Error(`CORS proxy failed with status ${proxyResponse.status}`);
                }
                blob = await proxyResponse.blob();
                console.log(`[FileService] CORS proxy succeeded: ${blob.size} bytes`);
              } catch (proxyError) {
                console.error(`[FileService] CORS proxy also failed: ${proxyError}`);
                throw new Error(`Failed to download file: ${directError instanceof Error ? directError.message : 'Network error'}`);
              }
            }
            
            return {
              id: track.id ? String(track.id) : `${Date.now()}-${idx}`,
              file: new File([blob], trackName, { type: 'audio/mp4' }),
              originalUrl: track.shareableUrl,
              shareableUrl: track.shareableUrl
            };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorStack = error instanceof Error ? error.stack : undefined;
            console.error(`Error loading file track-${track.id}: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
            throw error;
          }
        })
      );

      // Update the state with the processed files
      this.files = processedFiles;
      this.isLoading = false;
      
      console.log('[FileService] Shared setup loaded successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error(`[FileService] Error loading shared setup: ${errorMessage}`, errorStack ? `\n${errorStack}` : '');
      this.isLoading = false;
      throw error;
    }
  }
} 